// An area's evidence points on the Condition tab (block 8, piece 10): the
// views kept in the centre's register, to retake on this visit with the
// reference photo overlaid. Points are made from a photo (its full-screen
// view) once the job is in the register. Listing them needs signal.
import { useEffect, useState } from 'react';
import { Button, Label } from '@higgins/ui';
import { listEvidencePointsApi, type EvidencePointFromApi } from '../lib/api';
import type { DeviceRecord } from '../lib/device-db';
import { pointsOfArea } from '../lib/evidence-guide';
import { useOnline } from '../lib/hooks';
import { RetakeCamera } from './retake-camera';

function PointRow({ point, retaken, onRetake }: { point: EvidencePointFromApi; retaken: boolean; onRetake: () => void }) {
  return (
    <li className="flex flex-wrap items-center justify-between gap-2 py-1.5">
      <span className="min-w-0 text-sm">
        {point.name}
        <span className="block text-xs text-slate-500">{[point.buildingName, point.areaName].filter(Boolean).join(' · ')}{retaken ? ' · retaken on this visit' : ''}</span>
      </span>
      <Button variant="outline" size="sm" className="min-h-11" onClick={onRetake}>
        Retake
      </Button>
    </li>
  );
}

export function EvidencePointsSection({ visit, area, records }: { visit: DeviceRecord; area: DeviceRecord; records: DeviceRecord[] }) {
  const online = useOnline();
  const [points, setPoints] = useState<EvidencePointFromApi[] | null>(null);
  const [showOthers, setShowOthers] = useState(false);
  const [retaking, setRetaking] = useState<EvidencePointFromApi | null>(null);

  useEffect(() => {
    if (online) listEvidencePointsApi(visit.id).then((r) => setPoints(r.points), () => setPoints(null));
  }, [online, visit.id]);

  if (!points || points.length === 0) return null;
  const { mine, others } = pointsOfArea(points, { registerId: area.fields.hig_functionallocationid, name: area.fields.hig_name });
  // Retaken here: a photo of this job's naming the point.
  const retaken = new Set(records.filter((r) => r.table === 'photo' && typeof r.fields.hig_evidencepointid === 'string').map((r) => String(r.fields.hig_evidencepointid).toLowerCase()));
  const row = (p: EvidencePointFromApi) => <PointRow key={p.id} point={p} retaken={retaken.has(p.id.toLowerCase())} onRetake={() => setRetaking(p)} />;

  return (
    <div className="space-y-1 rounded-lg border border-slate-200 bg-white p-4">
      <Label>Evidence points</Label>
      <p className="text-xs text-slate-500">Views kept from earlier visits. Retake each from the same spot: last time&rsquo;s photo shows over the camera.</p>
      {mine.length > 0 ? <ul className="divide-y divide-slate-100">{mine.map(row)}</ul> : <p className="text-sm text-slate-500">None for this area.</p>}
      {others.length > 0 && (
        <div>
          <button type="button" className="min-h-11 text-sm text-primary underline" onClick={() => setShowOthers(!showOthers)}>
            {showOthers ? 'Hide' : 'Show'} the centre&rsquo;s other points ({others.length})
          </button>
          {showOthers && <ul className="divide-y divide-slate-100">{others.map(row)}</ul>}
        </div>
      )}
      {retaking && <RetakeCamera visitId={visit.id} jobId={area.jobId} areaId={area.id} point={retaking} onClose={() => setRetaking(null)} />}
    </div>
  );
}
